/*
 * 视觉小说模式。
 *
 * 旧版 chat/DialogBox.tsx + InteractionChat.tsx 的形态：底部一个粉边对话框，
 * 左上角骑一枚实心粉名牌，右下角「文字回复」。屏幕大部分留给立绘。
 *
 * ===========================================================================
 * 定高 + 翻页：为什么不照抄旧版的 textSplit
 * ===========================================================================
 *
 * 旧版把长回复**切成字符串数组**再逐段显示（packages/tools/src/string.ts 的
 * textSplit，按标点切、阈值是显示宽度 100 ≈ 50 汉字）。这里改成
 * **不切字符串，只平移行**：文字整段铺在一个 overflow:hidden 的定高视口里，
 * 翻页就是把内层往上推整数个页高。
 *
 * 换掉的四个理由，每条都对应旧版一个实际缺陷：
 *
 *   ① textSplit 的断点只有 `? ! 。 ？ ！ ； ; \n 空格` 九个。中文逗号、冒号、
 *      省略号都不算。一段全用「，」连接的长文本会让它**返回空数组**，框里
 *      显示的是上一条消息的残留。这里没有切分逻辑，也就没有这个失败模式。
 *
 *   ② 旧版 DialogBox 的文字容器绝对定位、**没有 overflow**，一旦切分失效
 *      文字直接溢出到框外面。它靠「框能装 146 字但每页只放 50 字」的三倍
 *      冗余来防御，代价是框常年空着三分之二。这里 overflow:hidden 在结构上
 *      杜绝溢出，于是每页可以填满。
 *
 *   ③ 旧版触发分段用 `.length`（字符数）、切分用显示宽度（汉字算 2），
 *      两个口径差一倍，99 个汉字整段不切。行偏移没有阈值，不存在口径问题，
 *      字号和屏宽变化也自动适应。
 *
 *   ④ **流式**。旧版把分段写在 `useEffect(..., [text])` 里，text 每变一次就
 *      重切并 setOrder(0)——接上 SSE 后每来一个 token 都会跳回第一段。
 *      行偏移天然抗流式：往后追加文字**不会改变前面已经断好的行**，
 *      只影响最后一个未满行，所以第 1 页的内容在整个流式过程中是稳定的。
 *
 * 代价：分页点落在**行边界**，可能把一句话切在两页之间；旧版切在标点上读起来
 * 顺一点。后续若要优化，可以在行边界附近回退到最近的标点，但那是纯增量。
 *
 * 流式填满一页时的行为：**停住，等用户点**。不自动前进——读得慢的人会被翻
 * 过去，整页错过。右下角出现继续箭头，后面的字静静缓入下一页。
 *
 * 与旧版的另一处差别：**可以往回翻**。旧版 order 只有 +1，而且重新进页面时
 * `isRead ? textList.length - 1 : 0` 直接跳到最后一段，前面的内容再也看不到。
 * 误点一下就永久丢一段话。
 *
 * ===========================================================================
 * 读完之前不放出口
 * ===========================================================================
 *
 * 「文字回复」与称呼输入框都只在**翻到最后一页**之后出现（`atEnd`）。
 *
 * 多页时把回复口摆在第 1 页，等于邀请用户打断一段他还没读到的话——
 * 点下去弹出输入框，后面几页他这辈子都不会再翻。这不是审美问题：
 * 开场白的最后一句是「我该怎么称呼你呀」，读不到那句，称呼框就是凭空冒出来的。
 *
 * 单页内容不受影响：`atEnd` 在单页时一渲染就为真。
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { IconBack, IconForward, IconPencil, IconRetry } from "../../components/Icons";
import { SpeakButton, type SpeakState } from "../../components/SpeakButton";
import { AiGeneratedMark } from "../../components/AiDisclosure";
import { TypingDots, TypingStatus } from "./MessageBubble";

/**
 * 按整页平移的翻页器。
 *
 * 页高取视口的 clientHeight。CSS 那边把视口高度定成 `行数 × 行高` 的整数倍，
 * 所以平移量恒为整数行——底部不会露出半行字，那看起来像渲染坏了。
 */
function usePager(contentKey: string) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [pageHeight, setPageHeight] = useState(0);

  const measure = useCallback(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;
    const height = viewport.clientHeight;
    if (height <= 0) return;
    setPageHeight(height);
    const total = Math.max(1, Math.ceil(content.scrollHeight / height));
    setPageCount(total);
    // 内容变短（换了条短消息、或失败提示替换了长回复）时把页码收回范围内，
    // 否则会停在一片空白上。
    setPage((current) => Math.min(current, total - 1));
  }, []);

  /*
   * ResizeObserver 盯的是内容高度，一次覆盖三件事：
   * 流式追加、字体加载完成后的重排、以及窗口/键盘引起的宽度变化。
   * 用 useLayoutEffect 是为了在浏览器绘制前就量完，避免先闪一下错的页数。
   */
  useLayoutEffect(() => {
    measure();
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [measure]);

  /*
   * 换了一条消息才回到第一页。
   *
   * 判据是 contentKey（消息 id），**不是 body 本身**——流式过程中 body 每来
   * 一个 token 就变一次，用它做依赖就会不停跳回开头，正是旧版那个 bug。
   */
  useEffect(() => {
    setPage(0);
  }, [contentKey]);

  const canPrev = page > 0;
  const canNext = page < pageCount - 1;

  return {
    viewportRef,
    contentRef,
    page,
    pageCount,
    pageHeight,
    canPrev,
    canNext,
    prev: useCallback(() => setPage((p) => Math.max(0, p - 1)), []),
    next: useCallback(
      () => setPage((p) => Math.min(pageCount - 1, p + 1)),
      [pageCount],
    ),
  };
}

export function TheaterView({
  name,
  sourceLabel,
  body,
  contentKey,
  pending,
  pendingText,
  streaming,
  onNameClick,
  speakState,
  onToggleSpeak,
  onReply,
  onRetry,
  atEndSlot,
  showAiGeneratedMark,
}: {
  name: string;
  /** 最新台词来自站外只读投影时，明确标出平台/端点与时间。 */
  sourceLabel?: string;
  /** 正文。可以是一条回复，也可以是缈缈说的失败原因。 */
  body: ReactNode;
  /**
   * 当前这段正文的身份，通常是消息 id。
   * **变了才回到第一页**；流式追加时它必须保持不变。
   */
  contentKey: string;
  /** 一个字还没收到时，以独立三点占据正文区。 */
  pending: boolean;
  /** 后端确认正在调用工具时，显示在三点旁边的角色文案。 */
  pendingText?: string | null;
  /** 已有部分正文但本轮尚未完成时，把三点接在正文末尾。 */
  streaming: boolean;
  onNameClick: () => void;
  /**
   * 朗读当前这条台词。
   *
   * ⚠ **状态由上层持有**，与气泡模式共用同一个 `useSpeak`——同一时刻只允许
   * 一条消息在响，两种模式各管各的会让切模式时两个声音叠在一起。
   *
   * 不传表示这条读不了：宿主不支持播放、正文是失败提示、或者她还在流式
   * 说到一半（那条还没落库，合成拿不到 messageId 做幂等键）。
   * 那时按钮整个不出现，而不是出现一个点了没反应的按钮。
   */
  speakState?: SpeakState;
  onToggleSpeak?: () => void;
  /**
   * 不传就不画「文字回复」。
   * 连不上灵境时根本发不出话，摆一个按钮在那儿只会让人白点一次。
   *
   * ⚠ 传了也**未必**画：她的话没读完之前不画，见下方 `atEnd`。
   */
  onReply?: (() => void) | undefined;
  /**
   * 不传就不画「再试一次」。传了则**顶掉**「文字回复」，见下方 footer 的注释。
   *
   * 由调用方判断可重试性：4xx 重试没有意义，那种情况下不要传。
   */
  onRetry?: (() => void) | undefined;
  /**
   * 读完最后一页时停在对话框**正上方**的东西，目前是「怎么称呼你」的输入框。
   *
   * 出现时**顶掉「文字回复」**：她刚问完「我该怎么称呼你呀」，
   * 此刻该回答的是这个问题，摆一个「文字回复」在旁边等于给了两条岔路，
   * 而走另一条的人这辈子都不会再被问一次。
   *
   * 没读完不画——理由同 `onReply`。
   */
  atEndSlot?: ReactNode;
  /** 当前台词是模型生成且运营开关开启时，在对话框底部显示。 */
  showAiGeneratedMark?: boolean;
}) {
  const pager = usePager(contentKey);
  const { canPrev, canNext, prev, next } = pager;

  /**
   * 她这段话读完了没有。
   *
   * 多页时必须翻到最后一页；单页则一渲染就算读完。
   *
   * ⚠ **等待首字和流式正文都必须显式排除。** 两种状态下 `canNext` 都可能为 false；
   * 不排除的话「我帮你搜一下」会被当成「她说完了」，回复口会提前冒出来。
   */
  const atEnd = !pending && !streaming && !canNext;

  /*
   * 称呼框优先于「文字回复」，两者都要等读完。
   *
   * 失败态例外：`onRetry` 存在时右下角本来就归它，见 footer 里的注释。
   */
  const showAtEndSlot = Boolean(atEndSlot) && atEnd;
  const showReply = Boolean(onReply) && atEnd && !showAtEndSlot;

  /*
   * ⚠ 判据是 `onReply` 而不是 `showReply`。
   *
   * footer 有没有会改变 `.theater__box` 的高度（`--with-footer` 多加 4vh）。
   * 用 showReply 的话，加载中 → 读第 1 页 → 翻到末页，这条 footer 会两次出没，
   * 对话框跟着一涨一缩，像是渲染坏了。
   *
   * 所以只要**将来可能**出现回复口就把这一格占住，按钮本身该不画还是不画——
   * 右侧空着是可接受的，footer 下面那段注释里对「没有出路就不摆按钮」
   * 已经是同一个态度。
   */
  const hasFooter =
    pager.pageCount > 1 ||
    Boolean(onReply) ||
    Boolean(onRetry) ||
    Boolean(showAiGeneratedMark);

  return (
    <div className="theater">
      {/*
        称呼框停在对话框上方。放在 .theater__frame 外面是为了不被名牌压住——
        名牌是相对 frame 绝对定位的，frame 里再塞东西会被它盖住左上角。
      */}
      {showAtEndSlot ? <div className="theater__dock">{atEndSlot}</div> : null}

      <div className="theater__frame">
        {/*
          名牌。
          ⚠ 从 <button> 改成了 <div>，因为喇叭要长在名字右边（旧版就是这么排的），
          而按钮不能嵌套按钮——嵌了的话点喇叭会连带触发形态说明弹窗。
          现在外层只是那枚粉色药丸，里面两颗各自可点。
        */}
        <div className="theater__nameplate">
          <button
            type="button"
            className="theater__nameplate-name"
            onClick={onNameClick}
            title="她现在是什么状态"
          >
            {name}
          </button>
          {/*
            站外来源改由 TheaterView 上方的“相遇”折叠条统一显示。属性和原节点暂时
            保留，之后需要恢复单条来源提示时不用重建数据链路。

            {sourceLabel ? <span className="theater__source">{sourceLabel}</span> : null}
          */}
          {speakState !== undefined && onToggleSpeak !== undefined ? (
            <SpeakButton
              state={speakState}
              onToggle={onToggleSpeak}
            />
          ) : null}
        </div>

        <div
          className={`theater__box${hasFooter ? " theater__box--with-footer" : ""}`}
        >
          {/*
           * 点正文区翻下一页，是视觉小说的通用操作，旧版也是点整个框。
           * 但只挂在视口上，不挂在整个框上——否则点「文字回复」会连带翻页。
           */}
          <div
            ref={pager.viewportRef}
            className="theater__viewport"
            onClick={canNext ? next : undefined}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight" || event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                next();
              } else if (event.key === "ArrowLeft") {
                event.preventDefault();
                prev();
              }
            }}
            role={canNext ? "button" : undefined}
            tabIndex={pager.pageCount > 1 ? 0 : -1}
            aria-label={
              pager.pageCount > 1
                ? `第 ${pager.page + 1} 页，共 ${pager.pageCount} 页`
                : undefined
            }
          >
            <div
              ref={pager.contentRef}
              className={`theater__body${streaming ? " theater__body--streaming" : ""}`}
              /*
               * 必须用**像素**，不能用 translateY(-100%)。
               *
               * 百分比位移的基准是被变换元素**自己的高度**，也就是全文高度，
               * 不是视口高度。8 行的内容写 -100% 会一口气推上去 8 行，
               * 第 2 页直接空白——这个 bug 靠肉眼看截图才发现，
               * 数字上「页数=2」是对的，但画面上什么都没有。
               */
              style={{ transform: `translateY(-${pager.page * pager.pageHeight}px)` }}
            >
              {pending ? (
                <TypingStatus activityText={pendingText} />
              ) : (
                <>
                  {body}
                  {streaming ? <TypingDots inline label="仍在回复" /> : null}
                </>
              )}
            </div>
          </div>

          {/* 页码键与右侧动作位都可能没有（单页 + 连不上灵境）。
              两个都没有时整条 footer 不渲染，否则底下留一道空白横条。 */}
          {hasFooter ? (
            <div className="theater__footer">
              <div className="theater__footer-left">
                {pager.pageCount > 1 ? (
                  <div className="theater__pager">
                    <button
                      type="button"
                      className="theater__page-btn"
                      onClick={prev}
                      disabled={!canPrev}
                      aria-label="上一页"
                    >
                      <IconBack size={16} />
                    </button>
                    <span className="theater__page-num" aria-hidden>
                      {pager.page + 1}/{pager.pageCount}
                    </span>
                    <button
                      type="button"
                      className={`theater__page-btn${canNext ? " is-waiting" : ""}`}
                      onClick={next}
                      disabled={!canNext}
                      aria-label="下一页"
                    >
                      <IconForward size={16} />
                    </button>
                  </div>
                ) : null}
                {showAiGeneratedMark ? <AiGeneratedMark /> : null}
              </div>

              {/*
               * 右下角只有一格，「再试一次」压过「文字回复」。
               *
               * ===================================================================
               * 一、为什么「再试一次」在这里，而不在正文区里
               * ===================================================================
               *
               * 它原本是 MiaoNotice 自带的，跟着失败台词一起落在 .theater__viewport
               * 内部。那个位置有两个毛病，都是结构性的：
               *
               *   ① 视口挂着 onClick 翻页（见上面那个 div），按钮没有 stopPropagation，
               *      于是台词超过一页时点「再试一次」会**连带翻页**。
               *   ② 视口是 overflow:hidden 的定死 N 行高，而按钮还继承了 --vn-line
               *      那个数倍行距。台词一换行，按钮就被推进第 2 页——用户得先翻页
               *      才知道自己有出路。
               *
               * 挪到 footer，两个一起消失：这里在视口外面，不吃翻页点击，也不被裁。
               *
               * ===================================================================
               * 二、为什么是「优先级」而不是「互斥」
               * ===================================================================
               *
               * 看上去两者场景互斥（连得上给回复、连不上给重试），实际不是：它们的
               * 条件来自**两个不同的请求**——「文字回复」看引导接口有没有给出
               * conversationId，「再试一次」看历史消息接口有没有失败。引导成功、
               * 历史超时是完全可能同时发生的，那时两颗都够格出现。
               *
               * 这里让失败优先，是一个**主动决策**而非巧合：历史没拉回来就不该让人
               * 发话，新消息接在哪条 sequence 后面都说不清。所以别把这段"修"成
               * 互斥断言——重叠是常态，只是我们选择了先修复再说话。
               *
               * ===================================================================
               * 三、「文字回复」为什么用 showReply 而不是 onReply
               * ===================================================================
               *
               * 她的话没读完就不给回复口。多页时用户停在第 1 页，右下角摆着
               * 「文字回复」——那是在邀请他打断一段他还没读到的话；点下去弹出输入框，
               * 后面几页他这辈子都不会再翻。所以必须先读到最后一页。
               *
               * 单页不受影响：`atEnd` 在单页时一渲染就为真。
               */}
              {onRetry ? (
                <button type="button" className="theater__retry" onClick={onRetry}>
                  <IconRetry size={14} />
                  再试一次
                </button>
              ) : showReply && onReply ? (
                <button type="button" className="theater__reply" onClick={onReply}>
                  <IconPencil size={15} />
                  文字回复
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
